import { Pressable, Row, styled, SxProp, View } from 'dripsy';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactNative, { StyleSheet, ViewStyle } from 'react-native';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { TextBody } from '../../typography/UniTypography';

const Gradient = styled(LinearGradient)();

export interface SegmentSelectItem<T> {
  labelKey: string;
  value: T;
}

export interface SegmentSelectProps<T> {
  data: Array<SegmentSelectItem<T>>;
  value?: T;
  buttonWidth?: number;
  buttonHeight?: number;
  wrapperBackgroundColor?: string;
  buttonBackgroundColors?: Array<string>;
  buttonStyle?: ViewStyle;
  labelColorActive?: string;
  labelColorInactive?: string;
  onValueChange?: (value: T) => void;
}

export const SegmentSelect = <T extends unknown>({
  data,
  value,
  buttonWidth,
  buttonHeight,
  wrapperBackgroundColor = '#EFEFEF',
  buttonBackgroundColors = ['#FFFFFF', '#FFFFFF'],
  buttonStyle,
  labelColorActive = '#000000',
  labelColorInactive = '#808080',
  onValueChange
}: SegmentSelectProps<T>) => {
  const { t } = useTranslation('earn');

  const [containerWidth, setContainerWidth] = useState(0);

  const ANIMATION_DURATION = 50;
  const BUTTON_WIDTH = buttonWidth ? buttonWidth : containerWidth / data.length - 8;
  const DEFAULT_VALUE = value || data[0].value;
  const DEFAULT_INDEX = data.findIndex((item) => item.value === DEFAULT_VALUE);

  const buttonsGroupRef = useRef<ReactNative.View>(null);
  const [selectedIndex, setSelectedIndex] = useState(DEFAULT_INDEX);

  useEffect(() => {
    onValueChange && onValueChange(data[selectedIndex].value);
  }, [selectedIndex]);

  const stylesSharedValue = useSharedValue({
    offset: selectedIndex,
    width: BUTTON_WIDTH
  });

  const animatedStyle = useAnimatedStyle(() => {
    const translateX = BUTTON_WIDTH * stylesSharedValue.value.offset;

    return {
      width: withTiming(BUTTON_WIDTH),
      transform: [
        {
          translateX: withTiming(translateX, { easing: Easing.sin, duration: ANIMATION_DURATION })
        }
      ]
    };
  }, [BUTTON_WIDTH]);

  function onPress(index: number) {
    stylesSharedValue.value = {
      offset: index,
      width: BUTTON_WIDTH
    };

    setTimeout(() => setSelectedIndex(index), ANIMATION_DURATION);
  }

  let buttonBackgroundGradient = buttonBackgroundColors;

  return (
    <View
      sx={{ ...styles.wrapper, backgroundColor: wrapperBackgroundColor }}
      onLayout={(event) => {
        setContainerWidth(event.nativeEvent.layout.width);
      }}>
      <Row sx={{ ...styles.container, height: buttonHeight || '$9' }}>
        <Animated.View style={[StyleSheet.absoluteFill, buttonStyle, animatedStyle]}>
          <Gradient
            colors={buttonBackgroundColors}
            start={{ x: 0.5, y: 0.0 }}
            end={{ x: 1, y: 0.0 }}
            sx={{
              height: buttonHeight || '$9',
              width: '100%',
              borderRadius: 100,
              shadowColor: '#000000',
              shadowOffset: {
                width: 0,
                height: 2
              },
              shadowOpacity: 0.15,
              shadowRadius: 8,
              elevation: 10
            }}
          />
        </Animated.View>
        <View ref={buttonsGroupRef} sx={{ flexDirection: 'row' }}>
          {data.map((item, index) => {
            let baseButtonStyle: SxProp = { ...styles.button, width: BUTTON_WIDTH, height: buttonHeight || '$9' };

            return (
              <Pressable key={`slide-radio-button-${item.labelKey}-${index}`} onPress={() => onPress(index)}>
                <View sx={baseButtonStyle}>
                  <TextBody bold sx={{ color: selectedIndex === index ? labelColorActive : labelColorInactive, textAlign: 'center' }}>
                    {t(item.labelKey)}
                  </TextBody>
                </View>
              </Pressable>
            );
          })}
        </View>
      </Row>
    </View>
  );
};

enum StyleName {
  wrapper = 'wrapper',
  container = 'container',
  button = 'button'
}

const styles: Record<StyleName, SxProp> = {
  wrapper: {
    padding: '$2',
    justifyContent: 'center',
    backgroundColor: '#EFEFEF',
    borderRadius: 100
  },
  container: {
    height: '$9',
    alignItems: 'center',
    backgroundColor: 'transparent'
  },
  button: {
    height: '$9',
    width: '$14',
    justifyContent: 'center',
    alignItems: 'center'
  }
};
